import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import type {
  GetGstrThreeBListPayloadType,
  GetGstrTwoAListPayloadType,
  GetGstrTwoBListPayloadType,
  GetInwardDataPayload,
  GetOutwardDataPayload,
  GstrOnePayload,
  GstrOnehsnWiseCountPayload,
  GstrThreeBInvoiceListPayload,
  PostGstrOneSaveDataType,
  GstrFilingSendOTPPayload,
  PostGstrOneCompleteFilingType,
  RequestGstOtpPayloadType,
  UpdateInwardDataPayload,
  UpdateOutwardDataPayload,
  VerifyGstOtpPayloadType,
  GetGstNumberPayload,
  ApproveGstPayloadType,
  GstCommentPayloadType,
  GstGetAllCommentPayloadType,
  DownloadGST2ADataPayload,
  DownloadGST2BDataPayload,
  DownloadMissingInvoicesPayload,
  DownloadGSTRExcelReportsType,
  PostGstrOneProceedType,
  GstrOneSatusType,
  GetGstrTwoATwoBComparePayloadType,
  ARNNumberPayloadType,
  UpdateInwardGstr2A2BStatusPayloadType,
  GetGstr2A2BSyncStatusPayloadType,
  GetAdminGstr1FilingReportPayloadType,
  GetGstrThreeBDetailsPayloadType,
  PostGstrThreeBFilingSaveDataType,
  PostGstrThreeBFilingCompleteFilingType,
  GstrDownloadJsonType,
  GstrThreeBCommonPayloadType,
  SaveCINNumberPayloadType,
  GetGstNoticesListPayloadType,
  GetGstNoticeDetailsPayloadType,
} from "@/commonTypes/gstTypes";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function getOutwardsListApi(
  data: GetOutwardDataPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    offset,
    limit,
    search_term,
    start_date,
    end_date,
    exports,
    sez,
    sez_tax_payment,
    place_of_supply,
    sales_type,
    sort_by,
    sort_order,
  } = data;
  return getData(`gst-module/outwards/list`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      offset,
      limit,
      ...(search_term && { search_term }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(exports && { exports }),
      ...(sez && { sez }),
      ...(sez_tax_payment && { sez_tax_payment }),
      ...(place_of_supply && { place_of_supply }),
      ...(sales_type && { sales_type }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
    },
  });
}

export async function updateOutwardsListApi(
  data: UpdateOutwardDataPayload
): Promise<AxiosResponse> {
  const { token, client_code, gst_outward_id, data_to_update } = data;

  const finalData = {
    client_code,
    gst_outward_id,
    data_to_update,
  };

  return postData(`gst-module/update/outwards/entry`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getInwardsListApi(
  data: GetInwardDataPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    offset,
    limit,
    search_term,
    start_date,
    end_date,
    gstr2a,
    gstr2b,
    seller_type,
    whether_eligible,
    purchase_from_composition_dealer,
    sort_by,
    sort_order,
    is_page_seen,
    notification_id,
  } = data;
  return getData(`gst-module/inwards/list`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      offset,
      limit,
      ...(search_term && { search_term }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(gstr2a && { gstr2a }),
      ...(gstr2b && { gstr2b }),
      ...(seller_type && { seller_type }),
      ...(whether_eligible && { whether_eligible }),
      ...(purchase_from_composition_dealer && {
        purchase_from_composition_dealer,
      }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(is_page_seen && { is_page_seen }),
      ...(notification_id && { notification_id }),
    },
  });
}

export async function updateInwardsListApi(
  data: UpdateInwardDataPayload
): Promise<AxiosResponse> {
  const { token, client_code, gst_inward_id, data_to_update } = data;

  const finalData = {
    client_code,
    gst_inward_id,
    data_to_update,
  };

  return postData(`gst-module/update/inwards/entry`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getGstOneListApi(
  data: GstrOnePayload
): Promise<AxiosResponse> {
  const { token, client_code, branch_id, finacial_year, month, gst_number } =
    data;
  return getData(`gst-module/gstr1-details`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      branch_id,
      financial_year: finacial_year,
      month,
      gst_number,
    },
  });
}

export async function getGstOneInvoiceListApi(
  data: GstrOnePayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    branch_id,
    finacial_year,
    month,
    gstr1_type,
    gst_number,
  } = data;
  return getData(`gst-module/gstr1/invoice/list`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      branch_id,
      financial_year: finacial_year,
      month,
      gstr1_type,
      gst_number,
    },
  });
}

export async function getGstThreeBInvoiceListAPi(
  data: GstrThreeBInvoiceListPayload
): Promise<AxiosResponse> {
  const {
    token,
    clientCode,
    branchId,
    month,
    gstr3bType,
    finacialYear,
    gst_number,
  } = data;
  const finalParams = {
    client_code: clientCode,
    branch_id: branchId,
    financial_year: finacialYear,
    month,
    gstr3b_type: gstr3bType,
    gst_number,
  };
  return getData(`gst-module/gstr3b/invoice/list`, {
    params: { ...finalParams },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function downloadOutwardsReportsApi(
  data: GetOutwardDataPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    offset,
    limit,
    search_term,
    start_date,
    end_date,
    exports,
    sez,
    sez_tax_payment,
    place_of_supply,
    sales_type,
    sort_by,
    sort_order,
  } = data;
  return getData(`gst-module/outwards/list/download`, {
    params: {
      client_code,
      offset,
      limit,
      ...(search_term && { search_term }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(exports && { exports }),
      ...(sez && { sez }),
      ...(sez_tax_payment && { sez_tax_payment }),
      ...(place_of_supply && { place_of_supply }),
      ...(sales_type && { sales_type }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadInwardsReportsApi(
  data: GetInwardDataPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    offset,
    limit,
    search_term,
    start_date,
    end_date,
    gstr2a,
    gstr2b,
    seller_type,
    whether_eligible,
    purchase_from_composition_dealer,
    sort_by,
    sort_order,
  } = data;
  return getData(`gst-module/inwards/list/download`, {
    params: {
      client_code,
      offset,
      limit,
      ...(search_term && { search_term }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(gstr2a && { gstr2a }),
      ...(gstr2b && { gstr2b }),
      ...(seller_type && { seller_type }),
      ...(whether_eligible && { whether_eligible }),
      ...(purchase_from_composition_dealer && {
        purchase_from_composition_dealer,
      }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadGST2AReportsApi(
  data: DownloadGST2ADataPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    data_type,
    gst_number,
    month,
    year,
    fetch_from_gst_portal,
  } = data;
  return getData(`gst/quicko-gst/get/documents/gstr2a/download`, {
    params: {
      client_code,
      data_type,
      gst_number,
      month,
      year,
      fetch_from_gst_portal,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadGST2BReportsApi(
  data: DownloadGST2BDataPayload
): Promise<AxiosResponse> {
  const { token, client_code, gst_number, month, year, fetch_from_gst_portal } =
    data;
  return getData(`gst/quicko-gst/get/documents/gstr2b/download`, {
    params: {
      client_code,
      gst_number,
      month,
      year,
      fetch_from_gst_portal,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadMissingInvoicesApi(
  data: DownloadMissingInvoicesPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    gst_number,
    start_date,
    end_date,
    type,
    data_type,
  } = data;
  return getData(`gst/quicko-gst/download/missing-invoices`, {
    params: {
      client_code,
      gst_number,
      start_date,
      end_date,
      type,
      data_type,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getGstrOnehsnWiseCountApi(
  data: GstrOnehsnWiseCountPayload
): Promise<AxiosResponse> {
  const { token, client_code, branch_id, month, gst_number } = data;
  return getData(`gst-module/gstr1/hsn-wise/count`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      branch_id,
      month,
      gst_number,
    },
  });
}

export async function getGstrTreeBListApi(
  data: GetGstrThreeBListPayloadType
): Promise<AxiosResponse> {
  const { token, clientCode, branchId, month, gst_number } = data;
  const finalData = {
    client_code: clientCode,
    branch_id: branchId,
    month,
    gst_number,
  };
  return getData("gst-module/gstr3b-details", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function requestGstrOtpApi(
  data: RequestGstOtpPayloadType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, gst_username } = data;
  const finalData = {
    gst_username,
    gst_number,
    client_code,
  };
  return postData("quicko-gst/send/otp/request", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function verifyGstrOtpApi(
  data: VerifyGstOtpPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, gst_number, gst_username, otp } = data;
  const finalData = {
    gst_number,
    client_code,
    gst_username,
    otp,
  };
  return postData("quicko-gst/verify/otp/request", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getGstr2aReturnsApi(
  data: GetGstrTwoAListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    clientCode,
    dataType,
    gstNumber,
    month,
    year,
    fetchFromGstPortal,
  } = data;
  const finalData = {
    client_code: clientCode,
    data_type: dataType,
    gst_number: gstNumber,
    month,
    year,
    fetch_from_gst_portal: fetchFromGstPortal,
  };
  return getData("gst/quicko-gst/get/documents/gstr2a", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function getGstr2a2bCompareApi(
  data: GetGstrTwoATwoBComparePayloadType
): Promise<AxiosResponse> {
  const { token, clientCode, gstNumber, month, year, type } = data;
  const finalData = {
    client_code: clientCode,
    gst_number: gstNumber,
    month,
    year,
    type,
  };
  return getData("gst/compare/gstr-2a/gstr-2b/expense/invoices", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getGstr2bReturnsApi(
  data: GetGstrTwoBListPayloadType
): Promise<AxiosResponse> {
  const { token, clientCode, gstNumber, month, year, fetchFromGstPortal } =
    data;
  const finalData = {
    client_code: clientCode,
    gst_number: gstNumber,
    month,
    year,
    fetch_from_gst_portal: fetchFromGstPortal,
  };
  return getData("gst/quicko-gst/get/documents/gstr2b", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postGstrOneFilingSaveDataApi(
  data: PostGstrOneSaveDataType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, year, month } = data;
  const finalData = {
    gst_number,
    client_code,
    year,
    month,
  };
  return postData("gst/quicko-gst/gstr1/save/data", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstrOneFilingResetSaveDataApi(
  data: PostGstrOneSaveDataType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, year, month } = data;
  const finalData = {
    gst_number,
    client_code,
    year,
    month,
  };
  return postData("gst/quicko-gst/gstr1/reset/save/data", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstrThreeFillingMappingApi(
  data: PostGstrOneSaveDataType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, year, month } = data;
  const finalData = {
    gst_number,
    client_code,
    year,
    month,
  };
  return postData("gst/gstr/map/gstr3b/data", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

// export async function gstOneFilingSendOTPApi(
//   data: GstrFilingSendOTPPayload
// ): Promise<AxiosResponse> {
//   const { token, gstin, client_code, year, month } = data;
//   const finalData = {
//     gstin,
//     client_code,
//     year,
//     month,
//   };

//   return getData("gst/quicko-gst/gstr1/proceed/file/otp/request", {
//     params: { ...finalData },
//     headers: {
//       "Content-Type": "application/json",
//       "user-token": token,
//     },
//   });
// }

export async function postOneFilingSendOTPApi(
  data: GstrFilingSendOTPPayload
): Promise<AxiosResponse> {
  const {
    token,
    client_code,

    gst_number,
    gstr_type,
    pan_number,
  } = data;
  const finalData = {
    client_code,

    gst_number,
    gstr_type,
    pan_number,
  };
  return postData("quicko-gst/send/evc-otp/request", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstOneCompleteFilingApi(
  data: PostGstrOneCompleteFilingType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, otp, month, year, pan_number } = data;
  const finalData = {
    gst_number,
    client_code,
    otp,
    month,
    year,
    pan_number,
  };
  return postData("gst/quicko-gst/gstr1/file", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstOneProceedFilingApi(
  data: PostGstrOneProceedType
): Promise<AxiosResponse> {
  const {
    token,
    gst_number,
    client_code,
    year,
    month,
    gstr_type,
    is_nil,
    is_gst_summary_fetched,
  } = data;
  const finalData = {
    gst_number,
    client_code,
    year,
    month,
    gstr_type,
    is_nil,
    is_gst_summary_fetched,
  };
  return postData("gst/quicko-gst/gstr1/proceed/file/get/summary", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getGstNumberListApi(
  data: GetGstNumberPayload
): Promise<AxiosResponse> {
  const { token, client_code } = data;
  const finalData = {
    client_code,
  };
  return getData("invoice-generation/address/unique/gst/list", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function approveGstApi(
  payload: ApproveGstPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, gstin, financial_year, gst_form_type, month } =
    payload;

  const finalData = {
    client_code,
    gstin,
    financial_year,
    gst_form_type,
    month,
  };

  return await postData(`gst/gstr/approval/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function gstPostComment(
  payload: GstCommentPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    comment,
    financial_year,
    gst_form_type,
    gstin,
    month,
  } = payload;

  const finalData = {
    client_code,
    comment,
    financial_year,
    gst_form_type,
    gstin,
    month,
  };

  return await postData(`gst/gstr/comments/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function gstGetAllCommentApi(
  data: GstGetAllCommentPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, financial_year, gst_form_type, gstin, month } =
    data;
  const finalData = {
    client_code,
    financial_year,
    gst_form_type,
    gstin,
    month,
  };
  return getData("gst/gstr/comments/details", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function GstrOneFilingSatusApi(
  data: GstrOneSatusType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    month,
    gst_number,
    year,
  };
  return getData("gst/quicko-gst/gstr1/get/filing/status", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postARNNumberApi(
  data: ARNNumberPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    acknowledgement_number,
    gstin,
    gstr_type,
    month,
    year,
  } = data;
  const finalData = {
    client_code,
    acknowledgement_number,
    gstin,
    gstr_type,
    month,
    year,
  };
  return postData("gst/gstr/acknowledgement-number/add", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function gstrOneDownloadJsonApi(
  data: GstrDownloadJsonType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    year,
    month,
    gst_number,
  };
  return getData("gst/quicko-gst/gstr1/save/json/payload/download", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function apiDownloadGSTRExcelReport(
  data: DownloadGSTRExcelReportsType
): Promise<AxiosResponse> {
  const { params, token } = data;
  return await getData("gst-module/gstr1-details/download", {
    params,
    responseType: "blob",

    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postUpdateInwardGstr2a2bStatusApi(
  data: UpdateInwardGstr2A2BStatusPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    client_code,
    start_date,
    end_date,
    data_type,
    fetch_from_gst_portal,
    gst_number,
  } = data;
  const finalData = {
    client_code,
    start_date,
    end_date,
    data_type,
    fetch_from_gst_portal,
    ...(gst_number && { gst_number }),
  };

  return postData(
    "gst/quicko-gst/dynamic/inward/update/gstr2a2b/status",
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getGstr2a2bSyncStatusApi(
  data: GetGstr2A2BSyncStatusPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return getData("gst/quicko-gst/gstr2a2b/job/status", {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAdminGstr1FilingReportApi(
  data: GetAdminGstr1FilingReportPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    month,
    year,
    client_code,
    gstin,
    filing_status,
    filing_source,
    search,
    filed_from,
    filed_to,
    sort_by,
    sort_order,
    limit,
    offset,
  } = data;
  return getData("admin-report/gstr1/filing-report", {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      month,
      year,
      ...(client_code && { client_code }),
      ...(gstin && { gstin }),
      ...(filing_status && { filing_status }),
      ...(filing_source && { filing_source }),
      ...(search && { search }),
      ...(filed_from && { filed_from }),
      ...(filed_to && { filed_to }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(limit && { limit }),
      ...(offset !== undefined && { offset }),
    },
  });
}

export async function getAdminGstr3bFilingReportApi(
  data: GetAdminGstr1FilingReportPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    month,
    year,
    client_code,
    gstin,
    filing_status,
    filing_source,
    search,
    filed_from,
    filed_to,
    sort_by,
    sort_order,
    limit,
    offset,
  } = data;
  return getData("admin-report/gstr3b/filing-report", {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      month,
      year,
      ...(client_code && { client_code }),
      ...(gstin && { gstin }),
      ...(filing_status && { filing_status }),
      ...(filing_source && { filing_source }),
      ...(search && { search }),
      ...(filed_from && { filed_from }),
      ...(filed_to && { filed_to }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(limit && { limit }),
      ...(offset !== undefined && { offset }),
    },
  });
}

export async function getGstrThreeBDetailsApi(
  data: GetGstrThreeBDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    month,
    gst_number,
    year,
  };
  return getData("gst/quicko-gst/gstr3b/get/details", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postGstrThreeBFilingSaveDataApi(
  data: PostGstrThreeBFilingSaveDataType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, year, month } = data;
  const finalData = {
    gst_number,
    client_code,
    year,
    month,
  };
  return postData("gst/quicko-gst/gstr3b/save/data", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstrThreeBFilingCompleteFilingApi(
  data: PostGstrThreeBFilingCompleteFilingType
): Promise<AxiosResponse> {
  const {
    token,
    gst_number,
    client_code,
    otp,
    month,
    year,
    pan_number,
    is_nil,
  } = data;
  const finalData = {
    gst_number,
    client_code,
    otp,
    month,
    year,
    pan_number,
    is_nil,
  };
  return postData("gst/quicko-gst/gstr3b/file", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

// gstr3b json download api

export async function gstrThreeBDownloadJsonApi(
  data: GstrDownloadJsonType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    year,
    month,
    gst_number,
  };
  return getData("gst/quicko-gst/gstr3b/save/json/payload/download", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function GstrThreeBFilingStatusApi(
  data: GstrThreeBCommonPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    month,
    gst_number,
    year,
  };
  return getData("gst/quicko-gst/gstr3b/get/filing/status", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function gstr3BCashItcBalanceApi(
  data: GstrThreeBCommonPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, month, gst_number, year } = data;
  const finalData = {
    client_code,
    month,
    gst_number,
    year,
  };
  return getData("gst/quicko-gst/gstr3b/get/cash-itc-balance", {
    params: { ...finalData },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postSaveCinNumberApi(
  data: SaveCINNumberPayloadType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, month, year, cin, amount } = data;
  const finalData = {
    gst_number,
    client_code,
    month,
    year,
    cin,
    amount,
  };
  return postData("gst/quicko-gst/gstr3b/save/cin", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function postGstr3bOffsetLiabilityApi(
  data: GstrThreeBCommonPayloadType
): Promise<AxiosResponse> {
  const { token, gst_number, client_code, month, year } = data;
  const finalData = {
    gst_number,
    client_code,
    month,
    year,
  };
  return postData("gst/quicko-gst/gstr3b/offset/liability", finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getGstNoticesListApi(
  data: GetGstNoticesListPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, gst_number, date, search_term } = data;
  return getData(`quicko-gst/notices`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      gst_number,
      ...(date && { date }),
      ...(search_term && { search_term }),
    },
  });
}

export async function getGstNoticeDetailsApi(
  data: GetGstNoticeDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, gst_number, reference_id } = data;
  return getData(`quicko-gst/notice/details`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      client_code,
      gst_number,
      reference_id,
    },
  });
}
